import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  CheckCircle, Circle, Loader2, AlertTriangle, 
  Sparkles, Tag, Pencil, ChevronDown, ChevronUp
} from 'lucide-react';

/**
 * Component for selecting the correct item from AI-identified candidates
 * Enables the "Is this your item?" flow for better accuracy
 */
export default function CandidateSelector({ 
  candidates, 
  imageQualityScore, 
  suggestions,
  onSelect, 
  onManualSearch,
  loading = false 
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  
  if (!candidates || candidates.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
        <p className="font-medium mb-2">Could not identify the item</p>
        <p className="text-sm text-muted-foreground mb-4">
          Try taking a clearer photo or search manually
        </p>
        <Button variant="outline" onClick={onManualSearch}>
          <Pencil className="w-4 h-4 mr-2" />
          Search Manually
        </Button>
      </div>
    );
  }
  
  const topCandidate = candidates[0];
  const otherCandidates = candidates.slice(1);
  const confidencePercent = Math.round(topCandidate.confidence * 100);
  
  return (
    <div className="space-y-4">
      {/* Image Quality Indicator (if low) */}
      {imageQualityScore < 0.5 && suggestions && suggestions.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Image quality could be better
          </p>
          <ul className="text-xs text-muted-foreground space-y-1">
            {suggestions.map((tip, i) => (
              <li key={i}>• {tip}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Main Question */}
      <div className="text-center">
        <h3 className="font-bold text-lg mb-1">Is this your item?</h3>
        <p className="text-sm text-muted-foreground">
          Select the best match or search manually
        </p>
      </div>
      
      {/* Top Candidate - Featured */}
      <div 
        className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer ${
          selectedIndex === 0 
            ? 'border-primary bg-primary/5' 
            : 'border-border hover:border-primary/50'
        }`}
        onClick={() => setSelectedIndex(0)}
      >
        {/* Selection Indicator */}
        <div className="absolute top-3 right-3">
          {selectedIndex === 0 ? (
            <CheckCircle className="w-6 h-6 text-primary" />
          ) : (
            <Circle className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
        
        {/* Best Match Badge */}
        <Badge className="mb-2 bg-primary/10 text-primary border-0">
          <Sparkles className="w-3 h-3 mr-1" />
          Best Match
        </Badge>
        
        {/* Item Details */}
        <div className="mb-3">
          <h4 className="font-bold text-lg pr-8">
            {topCandidate.brand} {topCandidate.model}
          </h4>
          {topCandidate.color && (
            <p className="text-sm text-muted-foreground">{topCandidate.color}</p>
          )}
        </div>
        
        {/* Category & Grading */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Badge variant="secondary">{topCandidate.category}</Badge>
          {topCandidate.is_graded && topCandidate.grading_info && (
            <Badge className="bg-amber-500 hover:bg-amber-600">
              {topCandidate.grading_info}
            </Badge>
          )}
        </div>
        
        {/* Confidence */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Confidence</span>
              <span className="font-medium">{confidencePercent}%</span>
            </div>
            <Progress value={confidencePercent} className="h-1.5" />
          </div>
        </div>
        
        {/* Description */}
        {topCandidate.description && (
          <p className="text-sm text-muted-foreground mt-3">
            {topCandidate.description}
          </p>
        )}
      </div>
      
      {/* Other Candidates */}
      {otherCandidates.length > 0 && (
        <div>
          <button 
            onClick={() => setShowAllCandidates(!showAllCandidates)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAllCandidates ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide other matches
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show {otherCandidates.length} other possible match{otherCandidates.length > 1 ? 'es' : ''}
              </>
            )}
          </button>
          
          {showAllCandidates && (
            <div className="space-y-2 mt-2">
              {otherCandidates.map((candidate, index) => {
                const actualIndex = index + 1;
                const isSelected = selectedIndex === actualIndex;
                const confPercent = Math.round(candidate.confidence * 100);
                
                return (
                  <div 
                    key={actualIndex}
                    className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedIndex(actualIndex)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {isSelected ? (
                          <CheckCircle className="w-5 h-5 text-primary" />
                        ) : (
                          <Circle className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h5 className="font-semibold truncate">
                          {candidate.brand} {candidate.model}
                        </h5>
                        
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {candidate.category}
                          </Badge>
                          {candidate.is_graded && candidate.grading_info && (
                            <Badge variant="outline" className="text-xs text-amber-600">
                              {candidate.grading_info}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {confPercent}% confidence
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="space-y-3 pt-2">
        <Button 
          onClick={() => onSelect(candidates[selectedIndex])}
          className="w-full h-12"
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Tag className="w-5 h-5 mr-2" />
              Get Price for This Item
            </>
          )}
        </Button>
        
        <Button 
          variant="ghost" 
          onClick={onManualSearch}
          className="w-full text-muted-foreground"
          disabled={loading}
        >
          <Pencil className="w-4 h-4 mr-2" />
          Not my item? Search manually
        </Button>
      </div>
    </div>
  );
}
